import { Link, useParams } from 'react-router-dom'

import { agentPath, decodeRouteToken } from '../observability/routing'
import { useConsoleView } from '../observability/state'

export function Agents() {
  const { snapshot } = useConsoleView()

  return (
    <section className="stack" aria-labelledby="agents-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Sessions</p>
          <h2 id="agents-title">Agents</h2>
          <p className="muted">Live intent, last activity and communication pressure for this exact server.</p>
        </div>
        <span className="count-badge">{snapshot.agents.length}</span>
      </div>

      <div className="list-grid">
        {snapshot.agents.map((agent) => (
          <article className="panel compact-panel" key={agent.name}>
            <div className="section-heading">
              <div>
                <p className="eyebrow">Step {agent.currentStep}</p>
                <h3>{agent.name}</h3>
              </div>
              <span className={`status status-${agent.status === 'active' ? 'healthy' : 'stale'}`}>
                {agent.status}
              </span>
            </div>
            <p>{agent.intent}</p>
            <dl className="detail-list compact-details">
              <div><dt>Last activity</dt><dd>{agent.lastActivity}</dd></div>
              <div><dt>Alerts</dt><dd>{agent.alertsPending}</dd></div>
              <div><dt>Replies due</dt><dd>{agent.messagesAwaitingReply}</dd></div>
            </dl>
            <Link className="text-link" to={agentPath(snapshot.instance.publicBaseUrl, agent.name)}>
              Session details
            </Link>
          </article>
        ))}
      </div>
    </section>
  )
}

export function AgentDetail() {
  const { snapshot } = useConsoleView()
  const { agentName } = useParams()
  const identity = decodeRouteToken(agentName)
  const agent = snapshot.agents.find((item) => item.name === identity)
  const communication = snapshot.communications.find((item) => item.name === identity)

  if (!agent) {
    return <section className="panel"><h2>Agent not found</h2><p className="muted">{identity}</p></section>
  }

  return (
    <section className="stack" aria-labelledby="agent-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Agent session</p>
          <h2 id="agent-title">{agent.name}</h2>
          <p className="muted">{agent.intent}</p>
        </div>
        <span className={`status status-${agent.status === 'active' ? 'healthy' : 'stale'}`}>{agent.status}</span>
      </div>

      <div className="two-column">
        <article className="panel">
          <h3>Session</h3>
          <dl className="detail-list">
            <div><dt>Current step</dt><dd>{agent.currentStep}</dd></div>
            <div><dt>Last activity</dt><dd>{agent.lastActivity}</dd></div>
            <div><dt>Session age</dt><dd>{agent.sessionAgeSeconds ?? 0}s</dd></div>
            <div><dt>Task</dt><dd>{agent.taskSummary ?? '—'}</dd></div>
          </dl>
          <div className="chip-row">
            {agent.workScope.map((scope) => <span className="chip" key={scope}>{scope}</span>)}
          </div>
        </article>

        <article className="panel">
          <h3>Communication</h3>
          <dl className="detail-list">
            <div><dt>Unread</dt><dd>{communication?.messagesAwaitingRead ?? agent.messagesAwaitingRead}</dd></div>
            <div><dt>Reply required</dt><dd>{communication?.messagesAwaitingReply ?? agent.messagesAwaitingReply}</dd></div>
            <div><dt>Alerts</dt><dd>{communication?.alertsPending ?? agent.alertsPending}</dd></div>
          </dl>
        </article>
      </div>

      <article className="panel">
        <div>
          <p className="eyebrow">Intent history</p>
          <h3>Session timeline</h3>
        </div>
        <div className="timeline">
          {(communication?.intentJournal ?? []).map((entry) => (
            <div className="timeline-row" key={`${entry.timestamp}-${entry.step}`}>
              <span className="timeline-dot" aria-hidden="true" />
              <div>
                <strong>Step {entry.step}</strong>
                <p>{entry.intent}</p>
                <p className="muted">{entry.timestamp}</p>
              </div>
            </div>
          ))}
          {(communication?.intentJournal.length ?? 0) === 0 && <p className="muted">No intent history in this snapshot.</p>}
        </div>
      </article>

      <article className="panel">
        <div>
          <p className="eyebrow">Messages</p>
          <h3>Communication journal</h3>
        </div>
        <div className="feed">
          {(communication?.messageJournal ?? []).map((message) => (
            <div className={`feed-row ${message.alert ? 'alert-row' : ''}`} key={message.messageHash}>
              <div>
                <strong>{message.senderName} · {message.state}</strong>
                <p>{message.text}</p>
                <p className="muted">{message.createdAt}</p>
              </div>
              {message.requireReply && <span className="count-badge">reply</span>}
            </div>
          ))}
          {(communication?.messageJournal.length ?? 0) === 0 && <p className="muted">No messages in this snapshot.</p>}
        </div>
      </article>
    </section>
  )
}
